import React, { useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getRedirectPath } from '../../types/navigation';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Shield, Loader2, AlertCircle } from 'lucide-react';

const Identity: React.FC = () => {
  const { isAuthenticated, isLoading, signIn, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignIn = useCallback(() => {
    clearError();
    signIn();
  }, [clearError, signIn]);

  useEffect(() => {
    if (isAuthenticated) {
      const redirectPath = getRedirectPath(location.state, '/dashboard');
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !isLoading) {
        handleSignIn();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLoading, handleSignIn]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-background via-background to-muted">
      <div className="w-full max-w-md space-y-6">
        {/* Branding */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4 animate-scale-in">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Welcome to Evo</h1>
          <p className="text-muted-foreground">Your Microsoft Copilot companion</p>
        </div>

        <Card className="shadow-xl border-0 bg-card/80 backdrop-blur animate-fade-in-up" style={{ animationDelay: '150ms' }}>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Sign In</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="text-center space-y-4">
              <p className="text-muted-foreground">
                Sign in with your Access account to continue.
              </p>
              <Button
                size="lg"
                className="w-full"
                onClick={handleSignIn}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : 'Sign in with Access Identity'}
              </Button>
            </div>

            <Separator />

            <p className="text-center text-xs text-muted-foreground">
              Powered by Access Identity
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Identity;
